import React from 'react';
import {
  Card,
  CardBody,
  VStack,
  HStack,
  Avatar,
  Badge,
  Text,
  Icon,
  IconButton,
  Flex,
  useColorModeValue,
  Tooltip,
  Wrap,
  WrapItem,
  Box,
} from '@chakra-ui/react';
import { EditIcon, ViewIcon } from '@chakra-ui/icons';
import { FaProjectDiagram, FaMapMarkerAlt, FaBuilding } from 'react-icons/fa';
import { PhoneIcon } from '@chakra-ui/icons';

const UserCard = ({ user, handleEditUser, handleRowClick, ...props }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'purple';
      case 'schemeAdmin':
        return 'blue';
      case 'talukaUser':
        return 'green';
      case 'subDivisionalOfficer':
        return 'orange';
      case 'departmentOfficer':
        return 'teal';
      case 'projectOfficer':
        return 'cyan';
      default:
        return 'gray';
    }
  };

  const formatRole = (role) => {
    switch (role) {
      case 'talukaUser':
        return 'Tehsildar';
      case 'schemeAdmin':
        return 'Office In Charge (OIC)';
      case 'admin':
        return 'Admin';
      case 'subDivisionalOfficer':
        return 'Sub-Divisional Officer (SDO)';
      case 'departmentOfficer':
        return 'Dept. Officer';
      case 'projectOfficer':
        return 'Project Officer (PO)';
      default:
        return role?.charAt(0).toUpperCase() + role?.slice(1) || 'N/A';
    }
  };

  // Helper function to get department names from array
  const getDepartmentNames = (department) => {
    if (!department) return ['N/A'];

    if (Array.isArray(department)) {
      return department.map((dept) =>
        typeof dept === 'object' ? dept.name || 'Department' : 'Department',
      );
    }

    // If it's a single object
    if (typeof department === 'object') {
      return [department.name || 'Department'];
    }

    return ['N/A'];
  };

  // Helper function to get scheme names
  const getSchemeNames = (schemes) => {
    if (!schemes || !Array.isArray(schemes)) return [];

    return schemes.map((scheme) =>
      typeof scheme === 'object' ? scheme.name || 'Scheme' : 'Scheme',
    );
  };

  // Helper function to get taluka names
  const getTalukaNames = (tahsils) => {
    if (!tahsils || !Array.isArray(tahsils)) return [];

    return tahsils.map((taluka) =>
      typeof taluka === 'object' ? taluka.name || 'Taluka' : 'Taluka',
    );
  };

  const departmentNames = getDepartmentNames(user.department);
  const schemeNames = getSchemeNames(user.schemes);
  const talukaNames = getTalukaNames(user.tahsils);

  return (
    <Card
      bg={cardBg}
      boxShadow="md"
      borderRadius="xl"
      overflow="hidden"
      borderTop="4px solid"
      borderTopColor={`${getRoleColor(user.role)}.500`}
      cursor="pointer"
      onClick={() => handleRowClick && handleRowClick(user)}
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }}
      height="100%"
      {...props}
    >
      <CardBody>
        <VStack align="start" spacing={3} height="100%">
          <Flex justify="space-between" w="full" align="center">
            <Avatar name={user.name} size="md" bg="blue.500" />
            <Badge
              colorScheme={user.active !== false ? 'green' : 'red'}
              fontSize="xs"
              px={2}
              py={0.5}
              borderRadius="md"
            >
              {user.active !== false ? 'Active' : 'Inactive'}
            </Badge>
          </Flex>

          <VStack align="start" spacing={1} w="full">
            <Text
              fontSize="lg"
              fontWeight="bold"
              color={textColor}
              noOfLines={1}
            >
              {user.name}
            </Text>
            <Text fontSize="sm" color={subtitleColor} noOfLines={1}>
              ID: {user.userId}
            </Text>
          </VStack>

          <Badge
            colorScheme={getRoleColor(user.role)}
            fontSize="xs"
            px={2}
            py={1}
            borderRadius="md"
          >
            {formatRole(user.role)}
          </Badge>

          <VStack align="start" spacing={2} w="full">
            {/* Department display with multiple badges */}
            <VStack align="start" spacing={1} w="full">
              <HStack spacing={2}>
                <Icon as={FaBuilding} color="blue.500" boxSize={3} />
                <Text fontSize="sm" color={textColor}>
                  Departments:
                </Text>
              </HStack>
              
              <Wrap spacing={1}>
                {departmentNames.slice(0, 3).map((dept, index) => (
                  <WrapItem key={index}>
                    <Badge
                      colorScheme="blue"
                      fontSize="xs"
                      variant="subtle"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                    >
                      {dept}
                    </Badge>
                  </WrapItem>
                ))}
                
                {departmentNames.length > 3 && (
                  <WrapItem>
                    <Tooltip
                      label={`${departmentNames.slice(3).join(', ')}`}
                      placement="top"
                      hasArrow
                      bg="gray.700"
                      color="white"
                      fontSize="xs"
                      p={2}
                    >
                      <Badge
                        colorScheme="gray"
                        fontSize="xs"
                        variant="subtle"
                        px={2}
                        py={0.5}
                        borderRadius="md"
                        cursor="help"
                      >
                        +{departmentNames.length - 3} more
                      </Badge>
                    </Tooltip>
                  </WrapItem>
                )}
              </Wrap>
            </VStack>

            <HStack spacing={2} w="full">
              <Icon as={PhoneIcon} color="green.500" boxSize={3} />
              <Text fontSize="sm" color={textColor} noOfLines={1}>
                {user.phoneNo || 'No Phone'}
              </Text>
            </HStack>
          </VStack>

          <Flex
            justify="space-between"
            w="full"
            pt={2}
            borderTop="1px dashed"
            borderColor={borderColor}
          >
            <VStack align="start" spacing={1}>
              {schemeNames.length > 0 && (
                <Tooltip
                  label={schemeNames.join(', ')}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="xs"
                  p={2}
                >
                  <HStack spacing={1} cursor="help">
                    <Icon as={FaProjectDiagram} color="green.500" boxSize={3} />
                    <Text fontSize="xs" color={subtitleColor}>
                      {schemeNames.length} Scheme(s)
                    </Text>
                  </HStack>
                </Tooltip>
              )}
              
              {talukaNames.length > 0 && (
                <Tooltip
                  label={talukaNames.join(', ')}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="xs"
                  p={2}
                >
                  <HStack spacing={1} cursor="help">
                    <Icon as={FaMapMarkerAlt} color="orange.500" boxSize={3} />
                    <Text fontSize="xs" color={subtitleColor}>
                      {talukaNames.length} Taluka(s)
                    </Text>
                  </HStack>
                </Tooltip>
              )}
            </VStack>
          </Flex>

          <Flex justify="flex-end" w="full" pt={2} mt="auto">
            <HStack spacing={2}>
              {handleEditUser && (
                <IconButton
                  aria-label="Edit User"
                  icon={<EditIcon />}
                  size="xs"
                  colorScheme="blue"
                  variant="ghost"
                  borderRadius="md"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditUser(user);
                  }}
                />
              )}
              {handleRowClick && (
                <IconButton
                  aria-label="View Details"
                  icon={<ViewIcon />}
                  size="xs"
                  colorScheme="green"
                  variant="ghost"
                  borderRadius="md"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowClick(user);
                  }}
                />
              )}
            </HStack>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default UserCard;